import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { LayoutDashboard, Building2, Users, ScrollText, Settings, Shield, LogOut, ChevronRight, AlertTriangle, Mail, FileText, Activity } from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Organisations', href: '/admin/organisations', icon: Building2 },
  { label: 'Users', href: '/admin/users', icon: Users },
  { label: 'Leads', href: '/admin/leads', icon: Mail },
  { label: 'Blog', href: '/admin/blog', icon: FileText },
  { label: 'Monitoring', href: '/admin/monitoring', icon: Activity },
  { label: 'Audit Log', href: '/admin/audit-log', icon: ScrollText },
  { label: 'Settings', href: '/admin/settings', icon: Settings },
];

export function AdminLayout({ children, title, breadcrumbs }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const impersonating = user?._impersonating;

  return (
    <div className="min-h-screen bg-[#F8FAFB]" data-testid="admin-layout">
      {/* Impersonation banner */}
      {impersonating && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white py-2 px-4 flex items-center justify-center gap-3 text-sm" data-testid="impersonation-banner">
          <AlertTriangle className="h-4 w-4" />
          <span>Impersonating <strong>{impersonating.org_name}</strong> as <strong>{impersonating.owner_name}</strong>. Read-only mode.</span>
          <button onClick={async () => { /* TODO: end impersonation */ navigate('/admin'); }}
            className="ml-2 px-3 py-0.5 bg-white text-red-600 rounded text-xs font-medium hover:bg-red-50" data-testid="end-impersonation-btn">
            End impersonation
          </button>
        </div>
      )}

      {/* Top bar */}
      <header className={`fixed ${impersonating ? 'top-9' : 'top-0'} left-0 right-0 z-50 bg-[#1a1a2e] border-b border-white/10 h-14 flex items-center px-6`} data-testid="admin-header">
        <Link to="/admin" className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#4DD0E1]" />
          <span className="text-white font-bold text-lg">Admin Console</span>
          <span className="text-[#4DD0E1] text-xs border border-[#4DD0E1]/30 rounded px-1.5 py-0.5">Platform</span>
        </Link>
        <div className="flex-1" />
        <Link to="/dashboard" className="text-[#B0BEC5] hover:text-white text-sm mr-4 transition-colors" data-testid="admin-back-to-product">
          Back to product
        </Link>
        <div className="flex items-center gap-2 text-[#B0BEC5]">
          <span className="text-sm">{user?.name}</span>
          <button onClick={async () => { await logout(); navigate('/signin'); }} className="p-1.5 hover:text-white transition-colors" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className={`flex ${impersonating ? 'pt-[5.25rem]' : 'pt-14'}`}>
        {/* Sidebar */}
        <aside className="fixed left-0 w-56 bg-white border-r border-[#E8ECF0] h-[calc(100vh-3.5rem)] overflow-y-auto" style={{ top: impersonating ? '5.25rem' : '3.5rem' }} data-testid="admin-sidebar">
          <nav className="py-4 px-3 space-y-0.5">
            {NAV_ITEMS.map(item => {
              const isActive = item.href === '/admin'
                ? location.pathname === '/admin'
                : location.pathname.startsWith(item.href);
              return (
                <Link key={item.href} to={item.href}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-[#0097A7]/10 text-[#0097A7] font-medium' : 'text-[#4E6974] hover:bg-[#F5F5F5] hover:text-[#0A2533]'}`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="ml-56 flex-1 p-6 min-h-[calc(100vh-3.5rem)]">
          {/* Breadcrumbs */}
          {breadcrumbs && (
            <div className="flex items-center gap-1.5 text-xs text-[#4E6974] mb-4" data-testid="admin-breadcrumbs">
              <Link to="/admin" className="hover:text-[#0097A7] transition-colors">Admin</Link>
              {breadcrumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <ChevronRight className="h-3 w-3 text-[#B0BEC5]" />
                  {c.href ? <Link to={c.href} className="hover:text-[#0097A7] transition-colors">{c.label}</Link> : <span className="text-[#0A2533] font-medium">{c.label}</span>}
                </span>
              ))}
            </div>
          )}
          {title && <h1 className="text-2xl font-bold text-[#0A2533] mb-6" data-testid="admin-page-title">{title}</h1>}
          {children}
        </main>
      </div>
    </div>
  );
}
